import { AlertTriangle, Briefcase, Building2, CheckCircle2, DollarSign, FolderKanban, Sparkles, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const STATUS_COLORS = { Planning: "#D4D4D8", "In Progress": "#002FA7", Blocked: "#E53935", Completed: "#10B981" };

const formatCurrency = (value) => value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `$${(value / 1000).toFixed(1)}K` : `$${value}`;
const formatDate = (value) => {
  if (!value) return "No date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const severityTone = {
  high: "border-red-200 bg-red-50 text-red-900",
  medium: "border-amber-200 bg-amber-50 text-amber-900",
  low: "border-blue-200 bg-blue-50 text-blue-900",
};

export const Dashboard = ({ data, onGenerateAiBrief, aiBriefLoading, onOpenProject }) => {
  if (!data) return <div className="empty-state"><p className="font-mono text-sm">Loading dashboard...</p></div>;

  const statusData = (data.projects_by_status || []).map((item) => ({ name: item.status, value: item.count, color: STATUS_COLORS[item.status] || "#A1A1AA" }));
  const budgetData = (data.budget_by_company || []).slice(0, 10).map((item) => ({ name: item.company_name?.length > 15 ? `${item.company_name.substring(0, 15)}...` : item.company_name, budget: item.total_budget }));
  const report = data.smart_report;
  const aiConfigured = report?.ai?.configured;
  const aiBrief = report?.ai_brief;

  return (
    <div data-testid="dashboard-view">
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Portfolio overview for companies, businesses, and projects.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-none border border-zinc-200 bg-white px-4 py-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
            Smart Reports
            <div className="mt-1 text-sm normal-case tracking-normal text-zinc-900">
              {report?.portfolio_health?.label || "N/A"} · score {report?.portfolio_health?.score ?? 0}/100
            </div>
          </div>
          <button className="btn-primary inline-flex items-center gap-2" disabled={aiBriefLoading} onClick={onGenerateAiBrief} type="button">
            <Sparkles size={14} />
            {aiBriefLoading ? "Generating..." : "Generate AI Brief"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4 mb-8">
        <StatCard label="Total Companies" value={data.total_companies || 0} icon={<Briefcase size={20} />} />
        <StatCard label="Total Businesses" value={data.total_businesses || 0} icon={<Building2 size={20} />} />
        <StatCard label="Total Projects" value={data.total_projects || 0} icon={<FolderKanban size={20} />} />
        <StatCard label="Total Budget" value={formatCurrency(data.total_budget || 0)} icon={<DollarSign size={20} />} />
      </div>

      {report ? (
        <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <section className="card p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Smart Summary</h2>
                <p className="mt-2 text-sm text-zinc-500">Auto-detected risks, workload pressure, and deadline drift from live portfolio data.</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-zinc-900">{report.portfolio_health?.score ?? 0}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{report.portfolio_health?.label || "N/A"}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <MiniMetric icon={<CheckCircle2 size={16} />} label="Completion" value={`${report.portfolio_health?.completion_rate ?? 0}%`} />
              <MiniMetric icon={<AlertTriangle size={16} />} label="Overdue" value={`${report.totals?.overdue_tasks ?? 0}`} />
              <MiniMetric icon={<Users size={16} />} label="Unassigned" value={`${report.totals?.unassigned_tasks ?? 0}`} />
              <MiniMetric icon={<FolderKanban size={16} />} label="Blocked" value={`${report.totals?.blocked_projects ?? 0}`} />
            </div>
            <div className="mt-6 grid gap-3">
              {(report.insights || []).map((item) => (
                <div key={`${item.title}-${item.severity}`} className={`border p-4 ${severityTone[item.severity] || severityTone.low}`}>
                  <div className="text-xs font-bold uppercase tracking-[0.2em]">{item.title}</div>
                  <p className="mt-1 text-sm leading-6">{item.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="card p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">AI Brief</h2>
                <p className="mt-2 text-sm text-zinc-500">
                  {aiConfigured ? `Generate a concise executive brief with ${report.ai?.model}.` : "AI is not configured yet. Add OPENAI_API_KEY on the backend to enable this."}
                </p>
              </div>
              <Sparkles className="text-zinc-400" size={18} />
            </div>
            {aiBrief ? (
              <div className="space-y-5">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Summary</div>
                  <p className="mt-2 text-sm leading-6 text-zinc-800">{aiBrief.summary}</p>
                </div>
                <ListBlock title="Priorities" items={aiBrief.priorities} />
                <ListBlock title="Risks" items={aiBrief.risks} />
                <ListBlock title="Opportunities" items={aiBrief.opportunities} />
                <div className="border-t border-zinc-200 pt-3 text-xs text-zinc-500">
                  Confidence: {aiBrief.confidence || "Not provided"}{aiBrief.generated_at ? ` · Generated ${formatDate(aiBrief.generated_at)}` : ""}
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-zinc-300 bg-zinc-50 p-5 text-sm text-zinc-500">
                {report.ai_brief_error || (aiConfigured ? "No AI brief generated yet." : "Server-side AI credentials are missing.")}
              </div>
            )}
          </section>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card p-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Projects by Status</h3>
          {statusData.length ? <div className="h-64"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">{statusData.map((entry, index) => <Cell key={index} fill={entry.color} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer></div> : <div className="h-64 flex items-center justify-center text-zinc-400 text-sm">No project data available</div>}
        </div>
        <div className="card p-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Budget by Company</h3>
          {budgetData.length ? <div className="h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={budgetData} layout="vertical" margin={{ left: 20 }}><CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" /><XAxis type="number" tickFormatter={formatCurrency} /><YAxis type="category" dataKey="name" width={100} /><Tooltip formatter={(value) => formatCurrency(value)} /><Bar dataKey="budget" fill="#002FA7" /></BarChart></ResponsiveContainer></div> : <div className="h-64 flex items-center justify-center text-zinc-400 text-sm">No budget data available</div>}
        </div>
      </div>

      {report ? (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <DataCard
            items={report.priority_projects}
            title="Priority Projects"
            emptyText="No active project pressure detected."
            onItemClick={(item) => onOpenProject?.(item.project_id)}
            renderItem={(item) => (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-zinc-900">{item.project_name}</div>
                  <div className="mt-1 text-sm text-zinc-500">{item.company_name} · {item.project_status || "Unknown status"} · Lead: {item.lead || "Unassigned"}</div>
                  <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">Open project</div>
                </div>
                <div className="text-right text-sm text-zinc-600">
                  <div>{item.overdue_task_count} overdue</div>
                  <div>{item.open_task_count} open</div>
                </div>
              </div>
            )}
          />
          <DataCard
            items={report.workload}
            title="Team Load"
            emptyText="Assign tasks to team members to see workload."
            renderItem={(item) => (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-zinc-900">{item.member_name}</div>
                  <div className="mt-1 text-sm text-zinc-500">{item.projects} projects · {item.known_team_member ? "Team member" : "Legacy assignee"}</div>
                </div>
                <div className="text-right text-sm text-zinc-600">
                  <div>{item.active_tasks} active</div>
                  <div>{item.overdue_tasks} overdue · {item.blocked_tasks} blocked</div>
                </div>
              </div>
            )}
          />
          <DataCard
            items={report.upcoming_deadlines}
            title="Upcoming Deadlines"
            emptyText="No near-term deadlines."
            onItemClick={(item) => onOpenProject?.(item.project_id, item.id)}
            renderItem={(item) => (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-zinc-900">{item.title}</div>
                  <div className="mt-1 text-sm text-zinc-500">{item.project_name} · {item.assignee || "Unassigned"}</div>
                  <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">Open task</div>
                </div>
                <div className="text-right text-sm text-zinc-600">
                  <div>{formatDate(item.due_date)}</div>
                  <div>{item.due_in_days} days</div>
                </div>
              </div>
            )}
          />
          <DataCard
            items={report.overdue_tasks}
            title="Overdue Tasks"
            emptyText="No overdue tasks."
            onItemClick={(item) => onOpenProject?.(item.project_id, item.id)}
            renderItem={(item) => (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-zinc-900">{item.title}</div>
                  <div className="mt-1 text-sm text-zinc-500">{item.project_name} · {item.assignee || "Unassigned"}</div>
                  <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-red-400">Open task</div>
                </div>
                <div className="text-right text-sm text-zinc-600">
                  <div>{formatDate(item.due_date)}</div>
                  <div>{Math.abs(item.due_in_days || 0)} days late</div>
                </div>
              </div>
            )}
          />
          <DataCard
            items={report.budget_hotspots}
            title="Budget Hotspots"
            emptyText="No budget data yet."
            renderItem={(item) => (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-zinc-900">{item.company_name}</div>
                  <div className="mt-1 text-sm text-zinc-500">{item.share_of_budget}% of tracked budget</div>
                </div>
                <div className="text-right text-sm text-zinc-600">{formatCurrency(item.total_budget)}</div>
              </div>
            )}
          />
        </div>
      ) : null}
    </div>
  );
};

const StatCard = ({ label, value, icon }) => <div className="stat-card"><div className="flex items-center justify-between mb-2"><span className="stat-label">{label}</span><span className="text-zinc-400">{icon}</span></div><div className="stat-value">{value}</div></div>;

const MiniMetric = ({ icon, label, value }) => (
  <div className="border border-zinc-200 bg-zinc-50 p-4">
    <div className="flex items-center justify-between text-zinc-500">
      <span className="text-xs font-bold uppercase tracking-[0.2em]">{label}</span>
      {icon}
    </div>
    <div className="mt-3 text-2xl font-bold text-zinc-900">{value}</div>
  </div>
);

const DataCard = ({ title, items = [], renderItem, emptyText, onItemClick }) => (
  <section className="card p-6">
    <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">{title}</h3>
    <div className="mt-4 space-y-3">
      {items.length ? items.map((item, index) => {
        const key = item.id || item.project_id || item.task_id || item.company_id || item.member_name || index;
        const clickable = typeof onItemClick === "function";
        if (clickable) {
          return (
            <button
              key={key}
              className="block w-full border border-zinc-200 bg-white p-4 text-left transition hover:border-zinc-400 hover:bg-zinc-50"
              onClick={() => onItemClick(item)}
              type="button"
            >
              {renderItem(item)}
            </button>
          );
        }
        return <div key={key} className="border border-zinc-200 bg-white p-4">{renderItem(item)}</div>;
      }) : <div className="border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm text-zinc-500">{emptyText}</div>}
    </div>
  </section>
);

const ListBlock = ({ title, items = [] }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{title}</div>
    {items.length ? (
      <ul className="mt-2 space-y-2 text-sm text-zinc-800">
        {items.map((item, index) => <li key={`${title}-${index}`} className="border-l-2 border-zinc-300 pl-3">{item}</li>)}
      </ul>
    ) : (
      <p className="mt-2 text-sm text-zinc-500">None.</p>
    )}
  </div>
);
